import { useState } from 'react'
import WireframeLayout, { buildSidebarSections } from '@/components/WireframeLayout'
import { cn, Badge, Card, Button, Progress, Separator, KPI } from '../components/primitives'
import { SKU_DATA, CAMPAIGNS, SCENARIOS } from '../components/data'

function SalesView() {
  const [sel, setSel] = useState("S2");
  const [exp, setExp] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-3 flex-wrap">
        <KPI label="Revenue (MTD)" value="$342K" delta="+18% vs last month" dir="up" />
        <KPI label="Active Campaigns" value="3" sub="1 proposed · 1 approved · 1 simulating" />
        <KPI label="Avg. Margin" value="59.4%" delta="-1.2pp from target" dir="down" />
        <KPI label="Inventory Feasibility" value="78%" sub="6 of 8 SKUs fully stocked" />
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Campaign Manager</h3>
          <Button size="sm">+ Propose Campaign</Button>
        </div>
        <div className="flex flex-col gap-3">
          {CAMPAIGNS.map(c => (
            <Card key={c.id} className="cursor-pointer hover:shadow-md transition-shadow">
              <div className="p-4" onClick={() => setExp(exp === c.id ? null : c.id)}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{c.name}</span>
                      <Badge variant={c.status}>{c.status}</Badge>
                      <Badge variant={c.feas}>{c.feas}</Badge>
                    </div>
                    <p className="text-xs text-slate-500">{c.ch} · {c.dates} · {c.disc} off</p>
                    {c.feas === "partial" && <p className="text-xs text-amber-600 font-medium mt-1">⚠ {c.note}</p>}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-slate-900">${(c.rev / 1000).toFixed(0)}K</div>
                    <div className="text-xs text-slate-400">{c.units.toLocaleString()} units</div>
                  </div>
                </div>
              </div>
              {exp === c.id && (
                <div className="px-4 pb-4">
                  <Separator className="mb-3" />
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <KPI sm label="Revenue Impact" value={`+$${(c.rev * 0.38 / 1000).toFixed(0)}K`} delta="+38% uplift" dir="up" />
                    <KPI sm label="Margin Impact" value="-3.2pp" delta="57.8% effective" dir="down" />
                    <KPI sm label="Stock Required" value={c.units.toLocaleString()} sub="units" />
                    <KPI sm label="ROI" value="4.2x" delta="vs 3.1x last" dir="up" />
                  </div>
                  <div className="flex gap-2"><Button variant="success" size="sm">Commit</Button><Button variant="violet" size="sm">Re-simulate</Button><Button variant="outline" size="sm">Adjust</Button></div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Revenue Scenario Simulator</h3>
        <div className="grid grid-cols-3 gap-3">
          {SCENARIOS.map(s => (
            <Card key={s.id} className={cn("p-5 cursor-pointer transition-all", sel === s.id ? "ring-2 ring-blue-300 bg-blue-50/40" : "hover:shadow-md")} onClick={() => setSel(s.id)} style={{}}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-bold text-slate-900">{s.name}</span>
                <Badge variant={s.risk === "Low" ? "healthy" : s.risk === "Medium" ? "warning" : "critical"}>{s.risk}</Badge>
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">{s.desc}</p>
              <div className="grid grid-cols-2 gap-2">
                <div><div className="text-xs text-slate-400 font-medium">Revenue</div><div className="text-base font-bold text-slate-900">${s.rev}</div></div>
                <div><div className="text-xs text-slate-400 font-medium">Margin</div><div className="text-base font-bold text-emerald-600">{s.margin}</div></div>
                <div><div className="text-xs text-slate-400 font-medium">Cash</div><div className="text-base font-bold text-amber-600">${s.cash}</div></div>
                <div><div className="text-xs text-slate-400 font-medium">SLA</div><div className="text-base font-bold text-blue-600">{s.sla}</div></div>
              </div>
              {sel === s.id && <div className="mt-3"><Button size="sm" className="w-full">Select Scenario →</Button></div>}
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Real-Time Inventory Feasibility</h3>
        <Card className="divide-y divide-slate-50">
          {SKU_DATA.filter(s => ["TikTok", "Shopee"].includes(s.ch)).map(s => (
            <div key={s.id} className="p-3.5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Badge variant={s.status}>{s.status}</Badge>
                <div><div className="text-sm font-medium text-slate-900">{s.name}</div><div className="text-xs text-slate-400">{s.id} · {s.ch}</div></div>
              </div>
              <div className="flex items-center gap-5">
                <div className="w-24"><div className="text-xs text-slate-400 mb-1">Stock Health</div><Progress value={s.dos} max={90} color={s.dos < 15 ? "#ef4444" : "#10b981"} /></div>
                <div className="text-right w-12"><div className="text-sm font-bold text-slate-900">{s.dos}d</div><div className="text-xs text-slate-400">left</div></div>
                <span className={cn("text-xs font-semibold", s.dos < 15 ? "text-red-600" : "text-emerald-600")}>{s.dos < 15 ? "⚠ Low" : "✓ OK"}</span>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard({ role }: { role: string }) {
  return (
    <WireframeLayout
      title="Sales Channel Console"
      subtitle="Propose campaigns & simulate outcomes"
      titleIcon="◎"
      titleColor="#3b82f6"
      sidebarLinks={buildSidebarSections(role, "dashboard")}
    >
      <SalesView />
    </WireframeLayout>
  )
}
